import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";

export interface LayoutState {
  sidebarCollapsed: boolean;
  mobileMenuOpen: boolean;
  darkMode: boolean;
  language: "en" | "pt";
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleMobileMenu: () => void;
  setMobileMenuOpen: (open: boolean) => void;
  toggleDarkMode: () => void;
  setDarkMode: (enabled: boolean) => void;
  setLanguage: (lang: "en" | "pt") => void;
}

const getInitialDarkMode = (): boolean => {
  if (typeof window !== "undefined") {
    const stored = localStorage.getItem("koachy-layout-store");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed.state?.darkMode !== undefined) {
          return parsed.state.darkMode;
        }
      } catch {
        return false;
      }
    }

    // First visit - check system preference
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  }

  return false;
};

const getBrowserLanguage = (): "en" | "pt" => {
  if (typeof window === "undefined") return "en";

  const browserLanguages = navigator.languages || [navigator.language];

  for (const lang of browserLanguages) {
    const baseLanguage = lang.split("-")[0].toLowerCase();
    if (baseLanguage === "pt" || baseLanguage === "en") {
      return baseLanguage as "pt" | "en";
    }
  }

  return "en";
};

export const useLayoutStore = create<LayoutState>()(
  devtools(
    persist(
      (set) => ({
        sidebarCollapsed: false,
        mobileMenuOpen: false,
        darkMode: getInitialDarkMode(),
        language: getBrowserLanguage(),

        toggleSidebar: () =>
          set(
            (state) => ({ sidebarCollapsed: !state.sidebarCollapsed }),
            false,
            "toggleSidebar",
          ),

        setSidebarCollapsed: (collapsed) =>
          set({ sidebarCollapsed: collapsed }, false, "setSidebarCollapsed"),

        toggleMobileMenu: () =>
          set(
            (state) => ({ mobileMenuOpen: !state.mobileMenuOpen }),
            false,
            "toggleMobileMenu",
          ),

        setMobileMenuOpen: (open) =>
          set({ mobileMenuOpen: open }, false, "setMobileMenuOpen"),

        toggleDarkMode: () =>
          set(
            (state) => {
              const newDarkMode = !state.darkMode;

              if (typeof document !== "undefined") {
                if (newDarkMode) {
                  document.documentElement.classList.add("dark");
                } else {
                  document.documentElement.classList.remove("dark");
                }
              }

              return { darkMode: newDarkMode };
            },
            false,
            "toggleDarkMode",
          ),

        setDarkMode: (enabled) =>
          set(
            () => {
              if (typeof document !== "undefined") {
                if (enabled) {
                  document.documentElement.classList.add("dark");
                } else {
                  document.documentElement.classList.remove("dark");
                }
              }

              return { darkMode: enabled };
            },
            false,
            "setDarkMode",
          ),

        setLanguage: (lang) => set({ language: lang }, false, "setLanguage"),
      }),
      {
        name: "koachy-layout-store",
        partialize: (state) => ({
          sidebarCollapsed: state.sidebarCollapsed,
          darkMode: state.darkMode,
          language: state.language,
        }),
        onRehydrateStorage: () => (state) => {
          if (state?.darkMode && typeof document !== "undefined") {
            document.documentElement.classList.add("dark");
          }
        },
      },
    ),
    {
      name: "layout-store",
    },
  ),
);
